/**
 * Used to generate highlighted matched text for the location autocomplete.
 * Return an array of objects representing result text and if that section matches the search input.
 *
 * @param {String} description The full item text
 * @param {Array} matchedSubstrings Array of {length, offset} objects indicating the portions of
 * `description` that are matches with input text
 * @return {Array} Array of {text, isMatch} objects.
 */
export default function toHighlightedParts(description, matchedSubstrings) {
  if (matchedSubstrings.length === 0) {
    return [
      {
        text: description,
        isMatch: false,
        idx: 0,
      },
    ];
  }

  const descriptionParts = [];
  let currentSubstringIndex = 0;
  let currentSubstring = matchedSubstrings[currentSubstringIndex];
  let currentCharacter = 0;

  // loop through our string and matching substrings to determine what needs to be wrapped in a
  // `strong` element
  while (currentCharacter < description.length) {
    let isMatch = false;
    let text = null;

    // if we're at the beginning of a match, get the full match substring and mark it as a match
    if (currentCharacter === currentSubstring.offset) {
      text = description.substring(currentCharacter, currentCharacter + currentSubstring.length);
      isMatch = true;
      currentCharacter += currentSubstring.length;
      currentSubstringIndex++;
      if (matchedSubstrings.length > currentSubstringIndex) {
        currentSubstring = matchedSubstrings[currentSubstringIndex];
      }

    // else if we're between matches, get all of the text between the two matches
    } else if (currentSubstringIndex !== matchedSubstrings.length) {
      text = description.substring(currentCharacter, currentSubstring.offset);
      currentCharacter = currentSubstring.offset;

    // else if we're at the beginning of the text, get all of the text between the start of the
    // string and the first match
    } else if (currentSubstringIndex === 0 && currentCharacter === 0) {
      text = description.substring(0, currentSubstring.offset);
      currentCharacter = currentSubstring.offset;

    // else we must be at the end of the text, get all of the remaining text
    } else {
      text = description.substring(currentCharacter);
      currentCharacter = description.length;
    }

    descriptionParts.push({
      text,
      isMatch,
      idx: currentCharacter,
    });
  }

  return descriptionParts;
}
